import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Plus, Edit, Trash2, Save, X, Check } from 'lucide-react';

export default function AdminBillingPlansTab({
  plans, showPlanForm, editingPlan, planForm, setPlanForm, handleSavePlan,
  setShowPlanForm, setEditingPlan, resetPlanForm, addFeature, updateFeature,
  removeFeature, startEditPlan, handleDeletePlan,
}) {
  return (
    <>
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Plans d'Abonnement</h2>
            <Button 
              onClick={() => {
                resetPlanForm();
                setEditingPlan(null);
                setShowPlanForm(true);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Créer un Plan
            </Button>
          </div>

          {/* Formulaire de création/édition de plan */}
          {showPlanForm && (
            <Card className="border-blue-500 border-2">
              <CardHeader>
                <CardTitle>
                  {editingPlan ? 'Modifier le Plan' : 'Créer un Nouveau Plan'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">ID du Plan *</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded"
                      value={planForm.plan_id}
                      onChange={(e) => setPlanForm({...planForm, plan_id: e.target.value})}
                      placeholder="ex: member_premium"
                      disabled={!!editingPlan}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Nom du Plan *</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded"
                      value={planForm.name}
                      onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                      placeholder="ex: Pack Premium"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Type *</label>
                    <select
                      className="w-full p-2 border rounded"
                      value={planForm.role}
                      onChange={(e) => setPlanForm({...planForm, role: e.target.value})}
                    >
                      <option value="member">Membre (Entreprise)</option>
                      <option value="conseiller">Conseiller ESS</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Zone *</label>
                    <select
                      className="w-full p-2 border rounded"
                      value={planForm.zone}
                      onChange={(e) => setPlanForm({...planForm, zone: e.target.value})}
                    >
                      <option value="metropole">France Métropole</option>
                      <option value="dom">DOM (Guadeloupe, Martinique...)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Prix Mensuel (€) *</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded"
                      value={planForm.price}
                      onChange={(e) => setPlanForm({...planForm, price: parseFloat(e.target.value)})}
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Prix Annuel (€)</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded"
                      value={planForm.price_annual}
                      onChange={(e) => setPlanForm({...planForm, price_annual: parseFloat(e.target.value)})}
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Crédits Mensuels Offerts</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded"
                      value={planForm.monthly_credits}
                      onChange={(e) => setPlanForm({...planForm, monthly_credits: parseInt(e.target.value)})}
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Cible (public)</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded"
                      value={planForm.target}
                      onChange={(e) => setPlanForm({...planForm, target: e.target.value})}
                      placeholder="ex: TPE, artisans..."
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium">Fonctionnalités</label>
                    <Button size="sm" variant="outline" onClick={addFeature}>
                      <Plus className="h-4 w-4 mr-1" />
                      Ajouter
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {planForm.features.map((feature, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          className="flex-1 p-2 border rounded"
                          value={feature}
                          onChange={(e) => updateFeature(index, e.target.value)}
                          placeholder="Description de la fonctionnalité"
                        />
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => removeFeature(index)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button onClick={handleSavePlan} className="flex-1">
                    <Save className="h-4 w-4 mr-2" />
                    Enregistrer
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowPlanForm(false);
                      setEditingPlan(null);
                      resetPlanForm();
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Annuler
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Liste des plans membres */}
          <div>
            <h3 className="text-xl font-bold mb-4">Plans Membres (Entreprises)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(plans.member_plans).map(([planId, plan]) => (
                <Card key={planId} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>{plan.name}</span>
                      <Badge className="bg-blue-700 text-white font-semibold">{plan.price}€/mois</Badge>
                    </CardTitle>
                    <CardDescription>{plan.target}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Prix annuel:</span>
                        <span className="font-medium">{plan.price_annual || plan.price * 10}€</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Crédits/mois:</span>
                        <span className="font-medium">{plan.monthly_credits || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Zone:</span>
                        <Badge className="bg-slate-700 text-white font-semibold">
                          {planId.includes('_dom') ? 'DOM' : 'Métropole'}
                        </Badge>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-gray-600">Fonctionnalités:</p>
                      {plan.features?.slice(0, 3).map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-1 text-xs">
                          <Check className="h-3 w-3 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600">{feature}</span>
                        </div>
                      ))}
                      {plan.features?.length > 3 && (
                        <p className="text-xs text-blue-600">
                          +{plan.features.length - 3} autres...
                        </p>
                      )}
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => startEditPlan(planId, plan)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Modifier
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleDeletePlan(planId)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Liste des plans conseillers */}
          <div>
            <h3 className="text-xl font-bold mb-4">Plans Conseillers ESS</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(plans.conseiller_plans).map(([planId, plan]) => (
                <Card key={planId} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>{plan.name}</span>
                      <Badge className={plan.price === 0 ? 'bg-green-600' : ''}>
                        {plan.price === 0 ? 'GRATUIT' : `${plan.price}€/mois`}
                      </Badge>
                    </CardTitle>
                    <CardDescription>{plan.target}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      {plan.price > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Prix annuel:</span>
                          <span className="font-medium">{plan.price_annual || plan.price * 10}€</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Crédits/mois:</span>
                        <span className="font-medium">{plan.monthly_credits || 0}</span>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-gray-600">Fonctionnalités:</p>
                      {plan.features?.slice(0, 3).map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-1 text-xs">
                          <Check className="h-3 w-3 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600">{feature}</span>
                        </div>
                      ))}
                      {plan.features?.length > 3 && (
                        <p className="text-xs text-blue-600">
                          +{plan.features.length - 3} autres...
                        </p>
                      )}
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => startEditPlan(planId, plan)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Modifier
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleDeletePlan(planId)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
    </>
  );
}
